import type { Point } from '../types/document'
import { skyness } from './nightGrade'

/**
 * Finding the roofline.
 *
 * The old attempt looked for a brightness edge with a Sobel filter, and on a
 * tile roof against a bright Florida sky there frequently isn't one: the roof
 * and the sky behind it can sit within a few levels of each other. It found
 * nothing on the test house and the button did nothing, which is worse than
 * not having the button.
 *
 * A roof edge is not a brightness edge. It is the boundary of the SKY, and we
 * already know exactly where the sky is, because the same mask drives the night
 * conversion and has to be right for that to work at all. So the roofline is
 * the bottom edge of the sky region, and it does not matter how bright the roof
 * is or how little it contrasts with the clouds behind it.
 *
 * What that boundary also includes is a neighbor's roof at the edge of frame
 * and any palm standing against the sky. Rather than guess which roof belongs
 * to the customer, every candidate is offered and the user clicks the one they
 * want: a wrong detection nobody clicks costs nothing.
 */

export interface RoofFacet {
  /** Normalized endpoints, ready to become a run. */
  a: Point
  b: Point
  /** Length as a fraction of the photo width, for ranking. */
  length: number
}

/** Working resolution. Enough to place a roofline, cheap enough to feel instant. */
const W = 420

/** Erode or dilate, treating everything outside the frame as part of the mask.
 *  Without that the top rows - which are pure sky - get eaten by the border. */
type Mask = Uint8Array<ArrayBuffer>

function morph(src: Mask, w: number, h: number, r: number, erode: boolean): Mask {
  const out = new Uint8Array(w * h) as Mask
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let hit = erode ? 1 : 0
      for (let dy = -r; dy <= r && hit === (erode ? 1 : 0); dy++) {
        for (let dx = -r; dx <= r; dx++) {
          // Clamp to the edge rather than treating outside as a fixed value.
          //
          // Using 1 there was the whole bug. It is right for erosion, where it
          // stops the top rows of pure sky being eaten by the border, but
          // during DILATION it invents sky along the bottom edge of the frame.
          // Every column then had its lowest sky pixel on the last row, the
          // "solid sky above" test failed everywhere, and the detector returned
          // nothing at all. Replicating the edge does the right thing for both.
          const nx = Math.min(w - 1, Math.max(0, x + dx))
          const ny = Math.min(h - 1, Math.max(0, y + dy))
          const v = src[ny * w + nx]
          if (erode ? !v : v) {
            hit = erode ? 0 : 1
            break
          }
        }
      }
      out[y * w + x] = hit
    }
  }
  return out
}

function median(values: number[]): number {
  if (!values.length) return 0
  const s = [...values].sort((a, b) => a - b)
  return s[s.length >> 1]
}

/** Ramer-Douglas-Peucker. The vertices it keeps are the roof's corners. */
function rdp(pts: Point[], eps: number): Point[] {
  if (pts.length < 3) return pts
  const a = pts[0]
  const b = pts[pts.length - 1]
  const ax = b.x - a.x
  const ay = b.y - a.y
  const n = Math.hypot(ax, ay) || 1
  let best = 0
  let bi = 0
  for (let i = 1; i < pts.length - 1; i++) {
    const d = Math.abs(ax * (pts[i].y - a.y) - ay * (pts[i].x - a.x)) / n
    if (d > best) {
      best = d
      bi = i
    }
  }
  if (best > eps) {
    const left = rdp(pts.slice(0, bi + 1), eps)
    const right = rdp(pts.slice(bi), eps)
    return left.slice(0, -1).concat(right)
  }
  return [a, b]
}

export function detectRoof(source: HTMLImageElement | HTMLCanvasElement): RoofFacet[] {
  const sw = 'naturalWidth' in source ? source.naturalWidth : source.width
  const sh = 'naturalHeight' in source ? source.naturalHeight : source.height
  if (!sw || !sh) return []

  const w = Math.min(W, sw)
  const h = Math.max(2, Math.round((sh * w) / sw))
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  const cx = c.getContext('2d', { willReadFrequently: true })
  if (!cx) return []
  cx.drawImage(source, 0, 0, w, h)
  const px = cx.getImageData(0, 0, w, h).data

  let sky = new Uint8Array(w * h) as Mask
  for (let y = 0; y < h; y++) {
    const yN = y / h
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4
      sky[y * w + x] = skyness(px[i], px[i + 1], px[i + 2], yN) > 0.5 ? 1 : 0
    }
  }

  // Close the holes a palm frond punches in the sky, then drop the specks a
  // bright wall leaves behind.
  sky = morph(morph(sky, w, h, 2, false), w, h, 2, true)
  sky = morph(morph(sky, w, h, 1, true), w, h, 1, false)

  /*
   * Keep only the largest connected region.
   *
   * The sky is one thing. A patch of bright wall read as cloud, or a gap
   * between two palm fronds, is not - and each of those has a lowest pixel of
   * its own that then poses as the roofline for its columns. Left in, they
   * corrupt the profile straight across the main roof, which is why the gable
   * never survived to become a facet.
   */
  {
    const seen = new Int32Array(w * h)
    const stack: number[] = []
    let bestLabel = 0
    let bestSize = 0
    let label = 0
    for (let i = 0; i < w * h; i++) {
      if (!sky[i] || seen[i]) continue
      label++
      let size = 0
      stack.push(i)
      seen[i] = label
      while (stack.length) {
        const p = stack.pop()!
        size++
        const px0 = p % w
        const py0 = (p / w) | 0
        for (const [dx, dy] of [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
        ]) {
          const nx = px0 + dx
          const ny = py0 + dy
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue
          const q = ny * w + nx
          if (sky[q] && !seen[q]) {
            seen[q] = label
            stack.push(q)
          }
        }
      }
      if (size > bestSize) {
        bestSize = size
        bestLabel = label
      }
    }
    if (bestLabel) for (let i = 0; i < w * h; i++) sky[i] = seen[i] === bestLabel ? 1 : 0
  }

  // Lowest sky pixel in each column, but only where the column is solidly sky
  // all the way up: a gap between two fronds must not read as the roofline.
  const prof = new Float32Array(w).fill(NaN)
  for (let x = 0; x < w; x++) {
    let last = -1
    let count = 0
    for (let y = 0; y < h; y++) {
      if (sky[y * w + x]) {
        last = y
        count++
      }
    }
    if (last < 3) continue
    if (count / (last + 1) > 0.85) prof[x] = last
  }

  // A palm in front of the roof drags the boundary up. Anything far from its
  // neighbors is not roof.
  const smoothed = new Float32Array(w)
  for (let x = 0; x < w; x++) {
    const win: number[] = []
    for (let d = -12; d <= 12; d++) {
      const v = prof[x + d]
      if (v !== undefined && !Number.isNaN(v)) win.push(v)
    }
    smoothed[x] = win.length ? median(win) : NaN
  }
  for (let x = 0; x < w; x++) {
    if (!Number.isNaN(prof[x]) && Math.abs(prof[x] - smoothed[x]) > h * 0.05) prof[x] = NaN
  }

  // Contiguous stretches, then corners, then one facet per span between them.
  const facets: RoofFacet[] = []
  let run: Point[] = []
  const flush = () => {
    if (run.length > 6) {
      const verts = rdp(run, Math.max(1.2, h * 0.006))
      for (let i = 1; i < verts.length; i++) {
        const a = verts[i - 1]
        const b = verts[i]
        const len = Math.hypot(b.x - a.x, b.y - a.y)
        if (len < w * 0.06) continue

        /*
         * How far the real boundary wanders from the straight line between the
         * two corners. A roof facet is flat; a frond edge is not.
         *
         * Judged on the 90th percentile, not the single worst pixel. Using the
         * maximum meant one stray column - a vent, an aerial, a leaf against
         * the sky - disqualified an otherwise perfectly straight gable, which
         * is why this found the neighbors' roofs and the porch ridge but not
         * the main roofline.
         */
        const k = (b.y - a.y) / Math.max(1e-6, b.x - a.x)
        const resid: number[] = []
        for (const p of run) {
          if (p.x < a.x || p.x > b.x) continue
          resid.push(Math.abs(p.y - (a.y + k * (p.x - a.x))))
        }
        if (!resid.length) continue
        resid.sort((m, n) => m - n)
        const wander = resid[Math.min(resid.length - 1, Math.floor(resid.length * 0.9))]
        if (wander > h * 0.02) continue

        facets.push({
          a: { x: a.x / w, y: a.y / h },
          b: { x: b.x / w, y: b.y / h },
          length: len / w,
        })
      }
    }
    run = []
  }
  for (let x = 0; x < w; x++) {
    if (Number.isNaN(prof[x])) flush()
    else run.push({ x, y: prof[x] })
  }
  flush()

  return facets.sort((p, q) => q.length - p.length)
}

/**
 * The roof edge a whole STROKE was aiming at.
 *
 * Straightening polishes the line the hand drew. That is the wrong ambition:
 * a swipe is not a measurement, it is a gesture that means "that roof edge,
 * there". Nudging it a few pixels onto the nearest gradient leaves it short at
 * both ends and keeps every kink the hand put in, which is how a run ends up
 * with six handles and a zigzag across a gable that is obviously one straight
 * line.
 *
 * The sky boundary already tells us where the real facets are and how far each
 * one runs. So match the stroke to one of them and take the WHOLE facet: the
 * user gets the edge they pointed at, full length, dead straight, whatever
 * their thumb actually did.
 *
 * Both tests have to pass. Close to the facet, and pointing the same way -
 * because a stroke down a gable rake passes close to the horizontal eave it
 * meets at the corner, and without the angle test it would snap to the wrong
 * one at the junction.
 */
export function facetForStroke(
  facets: RoofFacet[],
  pts: Point[],
  /**
   * Fraction of the photo width the stroke may sit off the facet.
   *
   * This was 0.07, which on a 2400px photo is 168 pixels - most of the height
   * of a roof. Draw along the ridge and, if the ridge itself was not one of
   * the detected facets, the run would be handed to whatever facet was within
   * that distance and land on the eave instead. Reported as: the line of
   * lights comes out lower than where I put the dot.
   *
   * A swipe is rough but it is not that rough. Nobody misses a roof edge by a
   * quarter of the roof, so a stroke that far from every facet is a stroke
   * about an edge we did not detect, and the honest answer there is to use the
   * line the user drew rather than to move it somewhere they did not.
   */
  tol = 0.025,
): RoofFacet | null {
  if (!facets.length || pts.length < 2) return null

  const a0 = pts[0]
  const b0 = pts[pts.length - 1]
  const strokeAng = Math.atan2(b0.y - a0.y, b0.x - a0.x)

  let best: RoofFacet | null = null
  let bestCost = Infinity

  for (const f of facets) {
    const vx = f.b.x - f.a.x
    const vy = f.b.y - f.a.y
    const len2 = vx * vx + vy * vy || 1

    let sum = 0
    for (const p of pts) {
      let t = ((p.x - f.a.x) * vx + (p.y - f.a.y) * vy) / len2
      t = Math.max(0, Math.min(1, t))
      sum += Math.hypot(f.a.x + vx * t - p.x, f.a.y + vy * t - p.y)
    }
    const meanDist = sum / pts.length
    if (meanDist > tol) continue

    // Direction, as an unsigned difference: drawn right-to-left is the same
    // edge as drawn left-to-right.
    const fAng = Math.atan2(vy, vx)
    let d = Math.abs(fAng - strokeAng) % Math.PI
    if (d > Math.PI / 2) d = Math.PI - d
    if (d > 0.45) continue // about 26 degrees

    /*
     * Distance dominates, and the length bonus is a tiebreak only.
     *
     * It used to be worth up to 0.2 of the cost against a distance term that
     * only reaches 1.0, so a long facet could out-argue a much closer one and
     * pull the run to the wrong edge. Preferring the longer of two equally
     * good matches is right; preferring it over a better match is not.
     */
    const cost = (meanDist / tol) * 2 + d / 0.45 - Math.min(0.5, f.length) * 0.15
    if (cost < bestCost) {
      bestCost = cost
      best = f
    }
  }
  return best
}

/** The facet nearest a click, in normalized coordinates. */
export function facetNear(facets: RoofFacet[], p: Point, tol = 0.06): RoofFacet | null {
  let best: RoofFacet | null = null
  let bestD = tol
  for (const f of facets) {
    const vx = f.b.x - f.a.x
    const vy = f.b.y - f.a.y
    const len2 = vx * vx + vy * vy || 1
    let t = ((p.x - f.a.x) * vx + (p.y - f.a.y) * vy) / len2
    t = Math.max(0, Math.min(1, t))
    const d = Math.hypot(f.a.x + vx * t - p.x, f.a.y + vy * t - p.y)
    if (d < bestD) {
      bestD = d
      best = f
    }
  }
  return best
}

/**
 * The facet, moved onto the line the user actually drew.
 *
 * Snapping a stroke to a detected facet gives a dead straight run of the right
 * length and direction, which is the whole point of it. What it should NOT do
 * is decide the height: the sky boundary is found on a 420px working copy with
 * a morphological close over it, and a scalloped barrel tile edge closes
 * DOWNWARD, so the facet sits a few working pixels below the real edge - about
 * twenty five on a 2400px photo. Measured: a run clicked on the ridge came out
 * twelve screen pixels low, and one clicked on a wall, where no facet matches,
 * came out dead on.
 *
 * Reported as: the line of lights is lower than where I put the dot.
 *
 * So the facet contributes its angle and its extent, and the stroke keeps its
 * own position. Slide the line sideways until it passes through the middle of
 * what was drawn. You get the straight, full length run you wanted, exactly
 * where you put it, which is the only place it can be right.
 */
export function facetThroughStroke(f: RoofFacet, pts: Point[]): RoofFacet {
  if (pts.length < 1) return f
  const vx = f.b.x - f.a.x
  const vy = f.b.y - f.a.y
  const len = Math.hypot(vx, vy) || 1
  // Unit normal to the facet.
  const nx = -vy / len
  const ny = vx / len

  let sum = 0
  for (const p of pts) sum += (p.x - f.a.x) * nx + (p.y - f.a.y) * ny
  const offset = sum / pts.length

  return {
    a: { x: f.a.x + nx * offset, y: f.a.y + ny * offset },
    b: { x: f.b.x + nx * offset, y: f.b.y + ny * offset },
    length: f.length,
  }
}
